'use client';

import { applicationPrefixMap, errorImageVariants, NEXT_PUBLIC_IMAGE_CDN_URL } from '@pickleballinc/lib/constants';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Application } from '@pickleballinc/lib/types';
import { cn } from '@pickleballinc/ui/lib/utils';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

interface ErrorProps {
	title: string;
	description?: string;
	/**
	 * The children elements, this will be displayed below the title and description. Usually buttons by design.
	 * The children are wrapped with a flex row container (column on mobile) with a gap of 2, so be sure to
	 * have the buttons full width on mobile to make them bigger, or don't, up to you.
	 * @example
	 * ```tsx
	 * import { Button } from '@pickleball/ui/button';
	 *
	 * <ErrorPage title="Page not found">
	 *  <Button>Go back</Button>
	 * </ErrorPage>
	 */
	children?: ReactNode;
	/**
	 * The theme color in which the character will be colored in. Defaults to pickleball purple.
	 * Currently has no effect since characters are now rendered from the CDN.
	 * @example
	 * ```tsx
	 * <ErrorPage title="Page not found" themeColor="#ff0000" />
	 */
	themeColor?: string;
	/**
	 * The status code of the error. Defaults to 404.
	 * This is used to determine which characters to display.
	 * Currently has no effect.
	 */
	code?: 404;
	titleClassName?: string;
	descriptionClassName?: string;
	customErrorCharacter?: ReactNode;
	/**
	 * Determines which application's character illustration is shown. Defaults to 'pickleball'.
	 */
	application?: Application;
}

/**
 * For an example of usage, see [not-found.tsx](https://github.com/pickleballinc/pickleball/blob/dev/apps/pickleball/src/app/not-found.tsx) on the Pickleball app.)
 * Some of the props contain further documentation, so hover over them to see it.
 */
export function ErrorPage(props: ErrorProps) {
	const { application = 'pickleball' } = props;

	const imageURL = NEXT_PUBLIC_IMAGE_CDN_URL;

	const [variant, setVariant] = useState<(typeof errorImageVariants)[number]>('404-error-image');

	useEffect(() => {
		setVariant(errorImageVariants[Math.floor(Math.random() * errorImageVariants.length)] ?? '404-error-image');
	}, []);

	const prefix = applicationPrefixMap[application];

	return (
		<div className="flex size-full min-h-[600px] flex-col items-center justify-start gap-8 overflow-hidden pt-12">
			{props.customErrorCharacter ? (
				props.customErrorCharacter
			) : (
				<img
					src={getCDNURL(`${imageURL}/global/404-error/${prefix}-${variant}.svg`, 640, 466)}
					alt={`${props.title} - Illustration`}
					className="w-full max-w-[300px]"
				/>
			)}
			<div className="flex flex-col items-center gap-2">
				<h2 className={cn('text-center text-[30px] font-semibold', props.titleClassName)}>{props.title}</h2>
				<p className={cn('text-center text-[20px] text-gray-600', props.descriptionClassName)}>{props.description}</p>
			</div>
			<div className="flex flex-col gap-2 md:flex-row">{props.children}</div>
		</div>
	);
}
